import { useState, useEffect, useRef } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Brain, Send, FileText, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function ChatSheet({ open, onOpenChange }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const msg = input.trim();
    setInput("");
    const nextMsgs = [...messages, { role: "user", content: msg }];
    setMessages(nextMsgs);
    setLoading(true);
    try {
      const { data } = await api.post("/chat/message", {
        session_id: sessionId,
        message: msg,
      });
      setSessionId(data.session_id);
      setMessages([
        ...nextMsgs,
        {
          role: "assistant",
          content: data.answer,
          citations: data.cited_document_names || [],
        },
      ]);
    } catch (e) {
      toast.error("Chat failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const reset = () => {
    setMessages([]);
    setSessionId(null);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[560px] p-0 flex flex-col bg-white border-l border-zinc-200"
        data-testid="chat-sheet"
      >
        <SheetHeader className="px-6 py-4 border-b border-zinc-200 bg-[#0A0A0A] text-white">
          <SheetTitle className="flex items-center gap-2 text-white font-heading">
            <Brain className="h-5 w-5 text-orange-500" />
            SECOND BRAIN <span className="overline text-zinc-500">— company docs only</span>
          </SheetTitle>
          <div className="text-xs text-zinc-400 font-mono">
            Answers strictly from your uploaded PDFs.
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4 bg-zinc-50">
          {messages.length === 0 && !loading && (
            <div className="text-center py-10">
              <Brain className="h-10 w-10 mx-auto text-zinc-300 mb-3" />
              <div className="text-sm text-zinc-500 max-w-xs mx-auto">
                Ask anything about your projects, DPRs, BOQs, agreements, invoices, quarry & royalty records.
                Upload documents first for best results.
              </div>
              <div className="mt-4 flex flex-wrap gap-2 justify-center">
                {[
                  "Summarise the latest DPR",
                  "Total royalty paid this year?",
                  "Vendor terms in latest agreement",
                ].map((s) => (
                  <button
                    key={s}
                    data-testid={`chat-suggestion-${s.slice(0, 8)}`}
                    onClick={() => setInput(s)}
                    className="text-xs px-3 py-1.5 border border-zinc-300 rounded-sm hover:border-orange-600 hover:text-orange-600 bg-white"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div
              key={i}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[85%] rounded-sm border px-4 py-3 text-sm ${
                  m.role === "user"
                    ? "bg-orange-600 text-white border-orange-600"
                    : "bg-white border-zinc-200 text-zinc-800"
                }`}
                data-testid={`chat-msg-${m.role}-${i}`}
              >
                <div className="whitespace-pre-wrap leading-relaxed">{m.content}</div>
                {m.citations?.length > 0 && (
                  <div className="mt-3 pt-2 border-t border-zinc-200 flex flex-wrap items-center gap-1">
                    <FileText className="h-3 w-3 text-zinc-500" />
                    {m.citations.slice(0, 5).map((n, idx) => (
                      <span key={idx} className="chat-citation">
                        {n}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex items-center gap-2 text-sm text-zinc-500 font-mono">
              <Loader2 className="h-4 w-4 animate-spin text-orange-600" />
              Searching company documents...
            </div>
          )}
          <div ref={endRef} />
        </div>

        <div className="border-t border-zinc-200 p-4 bg-white">
          <div className="flex items-end gap-2">
            <Textarea
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Ask about DPRs, BOQs, work orders, royalty, invoices..."
              rows={2}
              className="rounded-sm resize-none focus-visible:ring-orange-500 border-zinc-300"
            />
            <Button
              data-testid="chat-send-btn"
              onClick={send}
              disabled={loading || !input.trim()}
              className="bg-orange-600 hover:bg-orange-700 rounded-sm h-[60px] px-4"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex justify-between mt-2">
            <button
              data-testid="chat-new-session-btn"
              onClick={reset}
              className="text-xs text-zinc-500 hover:text-orange-600"
            >
              + New chat
            </button>
            <div className="text-[10px] font-mono text-zinc-400">
              Powered by Claude Sonnet 4.5 • context-only
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
